import styled, { keyframes } from "styled-components";

const Loader = () => (
  <LoaderWrapper>
    <LoaderSpinner />
  </LoaderWrapper>
);

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: 250px;
  position: relative;
`;

const LoaderSpinner = styled.div`
  border: 10px solid #f3f3f3;
  border-top: 10px solid #3498db;
  border-right: 10px solid #3498db;
  border-bottom: 10px solid #3498db;
  border-left: 10px 2px transparent; /* Small transparent gap to create "almost full circle" effect */
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export default Loader;

/* ─────────────────────────────────────────────────────────────────
   DASHBOARD SKELETON — full-page layout skeleton shown on first load
   ───────────────────────────────────────────────────────────────── */
export const DashboardSkeleton = () => (
  <DSkelPage>
    {/* ── Sidebar ── */}
    <DSkelSidebar>
      <DSkelUserSection>
        <DSkelBone $w="42px" $h="42px" $r="10px" />
        <DSkelUserInfo>
          <DSkelBone $w="120px" $h="13px" $r="5px" />
          <DSkelBone $w="72px" $h="10px" $r="5px" $delay="50ms" />
        </DSkelUserInfo>
      </DSkelUserSection>
      <DSkelMenuList>
        {Array.from({ length: 7 }).map((_, i) => (
          <DSkelMenuItem key={i}>
            <DSkelBone $w="18px" $h="18px" $r="5px" $delay={`${i * 45}ms`} />
            <DSkelBone
              $w={`${55 + ((i * 19) % 45)}px`}
              $h="11px"
              $r="5px"
              $delay={`${i * 45 + 20}ms`}
            />
          </DSkelMenuItem>
        ))}
      </DSkelMenuList>
    </DSkelSidebar>

    {/* ── Main ── */}
    <DSkelMain>
      {/* Header */}
      <DSkelHeader>
        <DSkelHeaderLeft>
          <DSkelBone $w="28px" $h="28px" $r="6px" />
          <DSkelBone $w="140px" $h="30px" $r="8px" $delay="30ms" />
        </DSkelHeaderLeft>
        <DSkelHeaderRight>
          <DSkelBone $w="148px" $h="32px" $r="8px" $delay="60ms" />
          <DSkelBone $w="36px" $h="36px" $r="50%" $delay="90ms" />
          <DSkelBone $w="36px" $h="36px" $r="50%" $delay="120ms" />
          <DSkelBone $w="36px" $h="36px" $r="50%" $delay="150ms" />
        </DSkelHeaderRight>
      </DSkelHeader>

      {/* Page content */}
      <DSkelContent>
        {/* Title row */}
        <DSkelTitleRow>
          <DSkelBone $w="160px" $h="22px" $r="7px" />
          <DSkelBone $w="90px" $h="22px" $r="20px" $delay="30ms" />
          <div style={{ flex: 1 }} />
          <DSkelBone $w="120px" $h="34px" $r="8px" $delay="60ms" />
        </DSkelTitleRow>

        {/* Hero cards */}
        <DSkelHeroRow>
          {[0, 1, 2].map((i) => (
            <DSkelBone
              key={i}
              $w="100%"
              $h="118px"
              $r="18px"
              $delay={`${i * 80}ms`}
            />
          ))}
        </DSkelHeroRow>

        {/* KPI strip */}
        <DSkelKpiRow>
          {Array.from({ length: 7 }).map((_, i) => (
            <DSkelKpiCard key={i}>
              <DSkelBone $w="28px" $h="28px" $r="7px" $delay={`${i * 35}ms`} />
              <DSkelKpiBody>
                <DSkelBone
                  $w="65px"
                  $h="9px"
                  $r="4px"
                  $delay={`${i * 35 + 15}ms`}
                />
                <DSkelBone
                  $w="44px"
                  $h="16px"
                  $r="5px"
                  $delay={`${i * 35 + 30}ms`}
                />
              </DSkelKpiBody>
            </DSkelKpiCard>
          ))}
        </DSkelKpiRow>

        {/* Table pair */}
        <DSkelTwoCol>
          {[0, 1].map((t) => (
            <DSkelTableCard key={t}>
              <DSkelBone $w="140px" $h="14px" $r="5px" $delay={`${t * 60}ms`} />
              <DSkelTableBody>
                {Array.from({ length: 6 }).map((_, r) => (
                  <DSkelTableRow key={r}>
                    <DSkelBone
                      $w={`${45 + (((t + r) * 13) % 30)}%`}
                      $h="10px"
                      $r="4px"
                      $delay={`${(t * 6 + r) * 35}ms`}
                    />
                    <DSkelBone
                      $w="60px"
                      $h="10px"
                      $r="4px"
                      $delay={`${(t * 6 + r) * 35 + 20}ms`}
                    />
                    <DSkelBone
                      $w="75px"
                      $h="10px"
                      $r="4px"
                      $delay={`${(t * 6 + r) * 35 + 40}ms`}
                    />
                  </DSkelTableRow>
                ))}
              </DSkelTableBody>
            </DSkelTableCard>
          ))}
        </DSkelTwoCol>
      </DSkelContent>
    </DSkelMain>
  </DSkelPage>
);

/* ── DashboardSkeleton styled components ── */

const dShimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const DSkelBone = styled.div<{
  $w: string;
  $h: string;
  $r: string;
  $delay?: string;
}>`
  width: ${(p) => p.$w};
  height: ${(p) => p.$h};
  border-radius: ${(p) => p.$r};
  flex-shrink: 0;
  animation: ${dShimmer} 1.6s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay || "0ms"};
  background: linear-gradient(90deg, #edf1f7 25%, #e2e8f0 50%, #edf1f7 75%);
  background-size: 600px 100%;
`;

const DSkelPage = styled.div`
  display: flex;
  width: 100vw;
  min-height: 100vh;
  background: #eef2f7;
`;

const DSkelSidebar = styled.div`
  width: 280px;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;
`;

const DSkelUserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
`;

const DSkelUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const DSkelMenuList = styled.div`
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DSkelMenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 8px;
`;

const DSkelMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const DSkelHeader = styled.div`
  height: 60px;
  background: #fff;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  gap: 12px;
  flex-shrink: 0;
`;

const DSkelHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DSkelHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DSkelContent = styled.div`
  flex: 1;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DSkelTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DSkelHeroRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

const DSkelKpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 12px;
`;

const DSkelKpiCard = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8f0f8;
  padding: 12px 13px;
  display: flex;
  align-items: flex-start;
  gap: 9px;
`;

const DSkelKpiBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const DSkelTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const DSkelTableCard = styled.div`
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e8f0f8;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const DSkelTableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const DSkelTableRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 9px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
  }
`;

type TableSkeletonProps = {
  rows?: number;
  columns?: number;
  /** Pass the same tableColumns array from the parent Table so widths are realistic */
  columnHints?: { wide?: boolean; narrow?: boolean; noWrap?: boolean }[];
};

export const TableSkeleton = ({
  rows = 10,
  columns = 7,
  columnHints = [],
}: TableSkeletonProps) => {
  const colCount = columnHints.length > 0 ? columnHints.length + 1 : columns;

  return (
    <SkeletonWrapper>
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <SkeletonRow key={rowIdx} $odd={rowIdx % 2 === 0}>
          {/* Index cell */}
          <SkeletonCell $width="36px" $flex="0 0 36px">
            <Bone $width="18px" $height="10px" $radius="4px" />
          </SkeletonCell>

          {/* Data cells */}
          {Array.from({ length: colCount - 1 }).map((_, colIdx) => {
            const hint = columnHints[colIdx] || {};
            const boneWidth = hint.narrow
              ? "48px"
              : hint.wide
                ? "80%"
                : `${52 + ((rowIdx * 3 + colIdx * 7) % 32)}%`;

            return (
              <SkeletonCell key={colIdx}>
                <Bone
                  $width={boneWidth}
                  $height="10px"
                  $radius="4px"
                  $delay={`${(rowIdx * colCount + colIdx) * 30}ms`}
                />
              </SkeletonCell>
            );
          })}
        </SkeletonRow>
      ))}
    </SkeletonWrapper>
  );
};

/* ─── Keyframes ─────────────────────────────────────────────────── */

const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

/* ─── Styled components ─────────────────────────────────────────── */

const SkeletonWrapper = styled.div`
  width: 100%;
`;

const SkeletonRow = styled.div<{ $odd: boolean }>`
  display: flex;
  align-items: center;
  background: ${(p) => (p.$odd ? "#fafbfd" : "#ffffff")};
  border-bottom: 1px solid #edf1f7;

  &:last-child {
    border-bottom: none;
  }
`;

const SkeletonCell = styled.div<{ $width?: string; $flex?: string }>`
  flex: ${(p) => p.$flex || "1"};
  width: ${(p) => p.$width || "auto"};
  padding: 9px 10px;
  border-right: 1px solid #e9edf3;

  &:last-child {
    border-right: none;
  }
`;

const Bone = styled.div<{
  $width: string;
  $height: string;
  $radius: string;
  $delay?: string;
}>`
  width: ${(p) => p.$width};
  height: ${(p) => p.$height};
  border-radius: ${(p) => p.$radius};
  animation: ${shimmer} 1.6s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay || "0ms"};
  background: linear-gradient(90deg, #edf1f7 25%, #e2e8f0 50%, #edf1f7 75%);
  background-size: 600px 100%;
`;
